import { AuthPackageStateEnum, HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '../../models';
import { LocDisplayType } from '../../models/display-type';
import { SYSTEM_ENUM_ROOT, SYSTEM_ENUM_TYPE } from '../shared-data';

const dpRoot = `${SYSTEM_ENUM_ROOT}|Auth Package State|`;

type SharedKeys = Pick<
	LocDef,
	'type' | 'portal' | 'displayType' | 'htmlAllowed' | 'portal' | 'shared' | 'feature'
>;

const sharedLocData: Pick<LocDef, 'displayType' | 'htmlAllowed' | 'shared' | 'feature'> = {
	displayType: `${SYSTEM_ENUM_TYPE}` as LocDisplayType,
	htmlAllowed: false,
	shared: true,
	feature: HubFeature.ExamAuthorization,
};

const sharedSpLocData: SharedKeys = {
	...sharedLocData,
	portal: 'SP',
};

const sharedCpLocData: SharedKeys = {
	...sharedLocData,
	portal: 'CP',
};

export type SPAuthPackageStateLocs = `SPAuthPackageStateEnum${AuthPackageStateEnum}`;

export const spAuthPackageStateEnumLocs: LocDefs<SPAuthPackageStateLocs> = {
	SPAuthPackageStateEnumAppointmentScheduled: {
		defaultValue: 'Scheduled',
		displayPath: `${dpRoot}${AuthPackageStateEnum.AppointmentScheduled}`,
		description: `Auth package state: Appointment is scheduled in the future`,
		legacyName: 'Exam.Authorization/System.Enum.AuthPackageState.AppointmentScheduled',
		...sharedSpLocData,
	},
	SPAuthPackageStateEnumExpired: {
		defaultValue: 'Expired',
		displayPath: `${dpRoot}${AuthPackageStateEnum.Expired}`,
		description: `Auth package state: Expiration date is in the past`,
		legacyName: 'Exam.Authorization/System.Enum.AuthPackageState.Expired',
		...sharedSpLocData,
	},
	SPAuthPackageStateEnumIneligible: {
		defaultValue: 'Ineligible',
		displayPath: `${dpRoot}${AuthPackageStateEnum.Ineligible}`,
		description: `Auth package state: Send OK date not set`,
		legacyName: 'Exam.Authorization/System.Enum.AuthPackageState.Ineligible',
		...sharedSpLocData,
	},
	SPAuthPackageStateEnumNoAuths: {
		defaultValue: 'No Authorizations',
		displayPath: `${dpRoot}${AuthPackageStateEnum.NoAuths}`,
		description: `Auth package state: No authorizations found`,
		legacyName: 'Exam.Authorization/System.Enum.AuthPackageState.NoAuths',
		...sharedSpLocData,
	},
	SPAuthPackageStateEnumOpen: {
		defaultValue: 'Open',
		displayPath: `${dpRoot}${AuthPackageStateEnum.Open}`,
		description: `Auth package state: Usable by the candidate`,
		legacyName: 'Exam.Authorization/System.Enum.AuthPackageState.Open',
		...sharedSpLocData,
	},
	SPAuthPackageStateEnumOpenCannotSendYet: {
		defaultValue: 'Open, Cannot Send Yet',
		displayPath: `${dpRoot}${AuthPackageStateEnum.OpenCannotSendYet}`,
		description: `Auth package state: Send OK date is in the past and with at least one individual authorization, or Send OK date is in the future`,
		legacyName: 'Exam.Authorization/System.Enum.AuthPackageState.OpenCannotSendYet',
		...sharedSpLocData,
	},
	SPAuthPackageStateEnumOpenWithAppointment: {
		defaultValue: 'Open with Appointment',
		displayPath: `${dpRoot}${AuthPackageStateEnum.OpenWithAppointment}`,
		description: `Auth package state: Send Ok Date is in the past, and the candiate has a valid, future, appointment scheduled`,
		...sharedSpLocData,
	},
	SPAuthPackageStateEnumRevoked: {
		defaultValue: 'Revoked',
		displayPath: `${dpRoot}${AuthPackageStateEnum.Revoked}`,
		description: `Auth package state: Eligibility has been revoked`,
		legacyName: 'Exam.Authorization/System.Enum.AuthPackageState.Revoked',
		...sharedSpLocData,
	},
	SPAuthPackageStateEnumUnsendable: {
		defaultValue: 'Unsendable',
		displayPath: `${dpRoot}${AuthPackageStateEnum.Unsendable}`,
		description: `Auth package state: Eligibility cannot be sent to the data provider`,
		legacyName: 'Exam.Authorization/System.Enum.AuthPackageState.Unsendable',
		...sharedSpLocData,
	},
	SPAuthPackageStateEnumUnusable: {
		defaultValue: 'Unusable',
		displayPath: `${dpRoot}${AuthPackageStateEnum.Unusable}`,
		description: `Auth package state: Eligibility is old and not usable`,
		legacyName: 'Exam.Authorization/System.Enum.AuthPackageState.Unusable',
		...sharedSpLocData,
	},
	SPAuthPackageStateEnumUpcoming: {
		defaultValue: 'Upcoming',
		displayPath: `${dpRoot}${AuthPackageStateEnum.Upcoming}`,
		description: `Auth package state: Send OK date is in the future`,
		legacyName: 'Exam.Authorization/System.Enum.AuthPackageState.Upcoming',
		...sharedSpLocData,
	},
	SPAuthPackageStateEnumUsed: {
		defaultValue: 'Used',
		displayPath: `${dpRoot}${AuthPackageStateEnum.Used}`,
		description: `Auth package state: Complete or aborted exam is tied to an associated authorization`,
		legacyName: 'Exam.Authorization/System.Enum.AuthPackageState.Used',
		...sharedSpLocData,
	},
};

export type CPAuthPackageStateLocs = `CPAuthPackageStateEnum${AuthPackageStateEnum}`;

export const cpAuthPackageStateEnumLocs: LocDefs<CPAuthPackageStateLocs> = {
	CPAuthPackageStateEnumAppointmentScheduled: {
		defaultValue: 'Appointment Scheduled',
		displayPath: `${dpRoot}${AuthPackageStateEnum.AppointmentScheduled}`,
		description: `Auth package state: Appointment is scheduled in the future`,
		legacyName: 'Exam.Authorization/System.Enum.AuthPackageState.AppointmentScheduled',
		...sharedCpLocData,
	},
	CPAuthPackageStateEnumExpired: {
		defaultValue: 'Expired',
		displayPath: `${dpRoot}${AuthPackageStateEnum.Expired}`,
		description: `Auth package state: Expiration date is in the past`,
		legacyName: 'Exam.Authorization/System.Enum.AuthPackageState.Expired',
		...sharedCpLocData,
	},
	CPAuthPackageStateEnumIneligible: {
		defaultValue: 'Ineligible',
		displayPath: `${dpRoot}${AuthPackageStateEnum.Ineligible}`,
		description: `Auth package state: Send OK date not set`,
		legacyName: 'Exam.Authorization/System.Enum.AuthPackageState.Ineligible',
		...sharedCpLocData,
	},
	CPAuthPackageStateEnumNoAuths: {
		defaultValue: 'No Authorizations',
		displayPath: `${dpRoot}${AuthPackageStateEnum.NoAuths}`,
		description: `Auth package state: No authorizations found`,
		legacyName: 'Exam.Authorization/System.Enum.AuthPackageState.NoAuths',
		...sharedCpLocData,
	},
	CPAuthPackageStateEnumOpen: {
		defaultValue: 'Open',
		displayPath: `${dpRoot}${AuthPackageStateEnum.Open}`,
		description: `Auth package state: Usable by the candidate`,
		legacyName: 'Exam.Authorization/System.Enum.AuthPackageState.Open',
		...sharedCpLocData,
	},
	CPAuthPackageStateEnumOpenCannotSendYet: {
		defaultValue: 'Open, Cannot Send Yet',
		displayPath: `${dpRoot}${AuthPackageStateEnum.OpenCannotSendYet}`,
		description: `Auth package state: Send OK date is in the past and with at least one individual authorization, or Send OK date is in the future`,
		legacyName: 'Exam.Authorization/System.Enum.AuthPackageState.OpenCannotSendYet',
		...sharedCpLocData,
	},
	CPAuthPackageStateEnumOpenWithAppointment: {
		defaultValue: 'Open with Appointment',
		displayPath: `${dpRoot}${AuthPackageStateEnum.OpenWithAppointment}`,
		description: `Auth package state: Send Ok Date is in the past, and the candiate has a valid, future, appointment scheduled`,
		...sharedCpLocData,
	},
	CPAuthPackageStateEnumRevoked: {
		defaultValue: 'Revoked',
		displayPath: `${dpRoot}${AuthPackageStateEnum.Revoked}`,
		description: `Auth package state: Eligibility has been revoked`,
		legacyName: 'Exam.Authorization/System.Enum.AuthPackageState.Revoked',
		...sharedCpLocData,
	},
	CPAuthPackageStateEnumUnsendable: {
		defaultValue: 'Unsendable',
		displayPath: `${dpRoot}${AuthPackageStateEnum.Unsendable}`,
		description: `Auth package state: Eligibility cannot be sent to the data provider`,
		legacyName: 'Exam.Authorization/System.Enum.AuthPackageState.Unsendable',
		...sharedCpLocData,
	},
	CPAuthPackageStateEnumUnusable: {
		defaultValue: 'Unusable',
		displayPath: `${dpRoot}${AuthPackageStateEnum.Unusable}`,
		description: `Auth package state: Eligibility is old and not usable`,
		legacyName: 'Exam.Authorization/System.Enum.AuthPackageState.Unusable',
		...sharedCpLocData,
	},
	CPAuthPackageStateEnumUpcoming: {
		defaultValue: 'Upcoming',
		displayPath: `${dpRoot}${AuthPackageStateEnum.Upcoming}`,
		description: `Auth package state: Send OK date is in the future`,
		legacyName: 'Exam.Authorization/System.Enum.AuthPackageState.Upcoming',
		...sharedCpLocData,
	},
	CPAuthPackageStateEnumUsed: {
		defaultValue: 'Used',
		displayPath: `${dpRoot}${AuthPackageStateEnum.Used}`,
		description: `Auth package state: Complete or aborted exam is tied to an associated authorization`,
		legacyName: 'Exam.Authorization/System.Enum.AuthPackageState.Used',
		...sharedCpLocData,
	},
};

export const AUTH_PACKAGE_STATE_ENUM_LOCS = MapLocKeys<
	SPAuthPackageStateLocs | CPAuthPackageStateLocs
>({
	...spAuthPackageStateEnumLocs,
	...cpAuthPackageStateEnumLocs,
});

export type SPAuthPackageStateEnumLocType = LocMap<SPAuthPackageStateLocs>;
export type CPAuthPackageStateEnumLocType = LocMap<CPAuthPackageStateLocs>;

export const spAuthPackageStateKeys: {
	[key in AuthPackageStateEnum]: keyof SPAuthPackageStateEnumLocType;
} = {
	[AuthPackageStateEnum.AppointmentScheduled]:
		AUTH_PACKAGE_STATE_ENUM_LOCS.SPAuthPackageStateEnumAppointmentScheduled,
	[AuthPackageStateEnum.Expired]: AUTH_PACKAGE_STATE_ENUM_LOCS.SPAuthPackageStateEnumExpired,
	[AuthPackageStateEnum.Ineligible]:
		AUTH_PACKAGE_STATE_ENUM_LOCS.SPAuthPackageStateEnumIneligible,
	[AuthPackageStateEnum.NoAuths]: AUTH_PACKAGE_STATE_ENUM_LOCS.SPAuthPackageStateEnumNoAuths,
	[AuthPackageStateEnum.Open]: AUTH_PACKAGE_STATE_ENUM_LOCS.SPAuthPackageStateEnumOpen,
	[AuthPackageStateEnum.OpenCannotSendYet]:
		AUTH_PACKAGE_STATE_ENUM_LOCS.SPAuthPackageStateEnumOpenCannotSendYet,
	[AuthPackageStateEnum.OpenWithAppointment]:
		AUTH_PACKAGE_STATE_ENUM_LOCS.SPAuthPackageStateEnumOpenWithAppointment,
	[AuthPackageStateEnum.Revoked]: AUTH_PACKAGE_STATE_ENUM_LOCS.SPAuthPackageStateEnumRevoked,
	[AuthPackageStateEnum.Unsendable]:
		AUTH_PACKAGE_STATE_ENUM_LOCS.SPAuthPackageStateEnumUnsendable,
	[AuthPackageStateEnum.Unusable]: AUTH_PACKAGE_STATE_ENUM_LOCS.SPAuthPackageStateEnumUnusable,
	[AuthPackageStateEnum.Upcoming]: AUTH_PACKAGE_STATE_ENUM_LOCS.SPAuthPackageStateEnumUpcoming,
	[AuthPackageStateEnum.Used]: AUTH_PACKAGE_STATE_ENUM_LOCS.SPAuthPackageStateEnumUsed,
};

export const cpAuthPackageStateKeys: {
	[key in AuthPackageStateEnum]: keyof CPAuthPackageStateEnumLocType;
} = {
	[AuthPackageStateEnum.AppointmentScheduled]:
		AUTH_PACKAGE_STATE_ENUM_LOCS.CPAuthPackageStateEnumAppointmentScheduled,
	[AuthPackageStateEnum.Expired]: AUTH_PACKAGE_STATE_ENUM_LOCS.CPAuthPackageStateEnumExpired,
	[AuthPackageStateEnum.Ineligible]:
		AUTH_PACKAGE_STATE_ENUM_LOCS.CPAuthPackageStateEnumIneligible,
	[AuthPackageStateEnum.NoAuths]: AUTH_PACKAGE_STATE_ENUM_LOCS.CPAuthPackageStateEnumNoAuths,
	[AuthPackageStateEnum.Open]: AUTH_PACKAGE_STATE_ENUM_LOCS.CPAuthPackageStateEnumOpen,
	[AuthPackageStateEnum.OpenCannotSendYet]:
		AUTH_PACKAGE_STATE_ENUM_LOCS.CPAuthPackageStateEnumOpenCannotSendYet,
	[AuthPackageStateEnum.OpenWithAppointment]:
		AUTH_PACKAGE_STATE_ENUM_LOCS.CPAuthPackageStateEnumOpenWithAppointment,
	[AuthPackageStateEnum.Revoked]: AUTH_PACKAGE_STATE_ENUM_LOCS.CPAuthPackageStateEnumRevoked,
	[AuthPackageStateEnum.Unsendable]:
		AUTH_PACKAGE_STATE_ENUM_LOCS.CPAuthPackageStateEnumUnsendable,
	[AuthPackageStateEnum.Unusable]: AUTH_PACKAGE_STATE_ENUM_LOCS.CPAuthPackageStateEnumUnusable,
	[AuthPackageStateEnum.Upcoming]: AUTH_PACKAGE_STATE_ENUM_LOCS.CPAuthPackageStateEnumUpcoming,
	[AuthPackageStateEnum.Used]: AUTH_PACKAGE_STATE_ENUM_LOCS.CPAuthPackageStateEnumUsed,
};
